import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, FileText, ClipboardCheck, Clock, Calendar } from "lucide-react";
import { INITIAL_STATE, DROPDOWN_FIELDS } from "../utils/constants";
import { parseCustomDate } from "../utils/helpers";

const cleanFieldNameMatch = (key) => {
    return String(key || "").trim().toLowerCase().replace(/[\s\._-]/g, "");
};

const toDateInputValue = (value) => {
    if (!value) return "";
    const str = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
        const [day, month, year] = str.split("-");
        return `${year}-${month}-${day}`;
    }
    if (str.includes("T")) return str.split("T")[0];
    const d = new Date(str);
    if (isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};



const toTitleCase = (str) => {
    if (!str) return "";
    if (str === "pp") return "Purchase Price";
    if (str === "tat") return "SLA TAT";
    return str
        .replace(/_/g, " ")
        .split(" ")
        .map(word => {
            const lower = word.toLowerCase();
            const uppercaseAcronyms = ["sla", "tat", "qc", "pp", "fha", "usda", "va", "vip"];
            if (uppercaseAcronyms.includes(lower)) {
                return word.toUpperCase();
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(" ");
};

const getInputType = (key) => {
    const normKey = cleanFieldNameMatch(key);
    if (normKey === "datecolumn" || normKey === "date" || normKey === "receiveddate") return "date";
    if (normKey === "intime" || normKey === "assigntime" || normKey === "outtime") return "text";
    return "text";
};

const getInputValue = (key, value) => {
    const normKey = cleanFieldNameMatch(key);
    if (normKey === "datecolumn" || normKey === "date" || normKey === "receiveddate") return toDateInputValue(value);
    if (normKey === "intime" || normKey === "assigntime" || normKey === "outtime") {
        if (value && typeof value === "string") {
            const d = parseCustomDate(value);
            if (d) {
                const day = String(d.getDate()).padStart(2, "0");
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const year = d.getFullYear();
                const hh = String(d.getHours()).padStart(2, "0");
                const min = String(d.getMinutes()).padStart(2, "0");
                return `${day}-${month}-${year} ${hh}:${min}`;
            }
        }
        return value || "";
    }
    return value || "";
};

const ManualEntryModal = ({ show, setShow, formData, handleManualInputChange, submitManualEntry, isManager = true }) => {
    const [liveUsersList, setLiveUsersList] = useState([]);

    useEffect(() => {
        if (!show) return;
        const fetchRoster = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get("/api/users/dropdown-list", {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data && Array.isArray(response.data)) {
                    setLiveUsersList(response.data);
                }
            } catch (err) {
                console.error("Failed to pull live dropdown users:", err);
            }
        };
        fetchRoster();
    }, [show]);

    if (!show) return null;

    const handleValueChangeIntercept = (e) => {
        const { name, value } = e.target;
        const normKey = cleanFieldNameMatch(name);
        let processedValue = value;
        if ((normKey === "intime" || normKey === "outtime") && value.includes("T")) {
            processedValue = value.replace("T", " ") + ":00";
        }
        handleManualInputChange({ target: { name, value: processedValue } });
    };

    // Segregate matching keys logically into categorized UI layout slots
    const allKeys = Object.keys(INITIAL_STATE).filter(k => k !== "sr_no" && k !== "id");

    const preferredMetaOrder = ["assigned_to", "priority", "client_name", "department", "reviewer_name", "lender_name", "product_code", "appraiser_name", "loan_type", "substatus", "status"];
    const metaKeysRaw = allKeys.filter(k => preferredMetaOrder.includes(k));
    const metaKeys = metaKeysRaw.sort((a, b) => {
        const idxA = preferredMetaOrder.indexOf(a);
        const idxB = preferredMetaOrder.indexOf(b);
        return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
    });

    const processingKeys = allKeys.filter(k => ["pp", "market_value", "ten_percent", "lower_than_contract", "crosscheck_results", "revisions", "sla_status", "feedback", "feedback2", "training", "training_findings", "auditor_findings", "qc_by"].includes(k));
    
    const preferredLoggingOrder = ["date_column", "file_number", "loan_number", "address", "in_time", "assign_time", "out_time", "tat", "reviewer_tat", "shift", "day", "production_log", "notes_message", "comments"];
    const loggingKeysRaw = allKeys.filter(k => !metaKeys.includes(k) && !processingKeys.includes(k));
    const loggingKeys = loggingKeysRaw.sort((a, b) => {
        const idxA = preferredLoggingOrder.indexOf(a);
        const idxB = preferredLoggingOrder.indexOf(b);
        return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
    });

    const renderFieldCard = (key) => {
        const normKey = cleanFieldNameMatch(key);
        const isWide = ["address", "comments", "notes_message", "crosscheck_results", "training_findings", "auditor_findings", "advisor_note"].includes(normKey);

        const inputContainerStyle = {
            gridColumn: isWide ? "span 3" : "span 1",
            display: "flex",
            flexDirection: "column",
            gap: "5px"
        };

        const labelStyle = {
            fontSize: "12px",
            fontWeight: "600",
            color: "#475569",
            letterSpacing: "0.5px"
        };

        const fieldBaseStyle = {
            width: "100%",
            padding: "10px 14px",
            borderRadius: "6px",
            border: "1px solid #cbd5e1",
            fontSize: "14px",
            color: "#1e293b",
            outline: "none",
            backgroundColor: "#f8fafc",
            transition: "all 0.2s ease",
            boxSizing: "border-box"
        };

        const adminMandatoryKeys = ["file_number", "loan_number", "date_column", "in_time", "assign_time"];
        const fullMandatoryKeys = ["file_number", "loan_number", "date_column", "client_name", "address", "assigned_to", "in_time", "assign_time", "out_time"];
        const mandatoryKeys = isManager ? adminMandatoryKeys : fullMandatoryKeys;
        const isMandatory = mandatoryKeys.includes(key);

        return (
            <div key={key} style={inputContainerStyle}>
                <label style={labelStyle}>
                    {toTitleCase(key)}
                    {isMandatory && <span style={{ color: "#ef4444", marginLeft: "4px", fontWeight: "bold" }}>*</span>}
                </label>
                {normKey === "assignedto" || normKey === "reviewername" || normKey === "employee" ? (
                    <select
                        name={key}
                        value={formData[key] || ""}
                        onChange={handleValueChangeIntercept}
                        style={fieldBaseStyle}
                        className="modal-select-input"
                        disabled={normKey === "reviewername"}
                    >
                        <option value="">Select Employee</option>
                        {liveUsersList.map(user => (
                            <option key={user} value={user}>{user}</option>
                        ))}
                    </select>
                ) : normKey === "status" ? (
                    <input
                        type="text"
                        name={key}
                        value={formData[key] || ""}
                        readOnly
                        style={{ ...fieldBaseStyle, backgroundColor: "#e2e8f0", cursor: "not-allowed" }}
                        className="modal-text-input"
                        placeholder="Auto-derived from Substatus"
                    />
                ) : DROPDOWN_FIELDS[key] ? (
                    <select
                        name={key}
                        value={formData[key] || ""}
                        onChange={handleValueChangeIntercept}
                        style={fieldBaseStyle}
                        className="modal-select-input"
                    >
                        <option value="">Select</option>
                        {DROPDOWN_FIELDS[key].map(o => (
                            <option key={o} value={o}>{o}</option>
                        ))}
                    </select>
                ) : normKey === "intime" || normKey === "assigntime" || normKey === "outtime" ? (
                    <div style={{ display: "flex", gap: "6px", width: "100%", position: "relative" }}>
                        <input
                            type="text"
                            name={key}
                            value={getInputValue(key, formData[key])}
                            onChange={handleValueChangeIntercept}
                            style={{ ...fieldBaseStyle, flex: 1, paddingRight: "38px" }}
                            className="modal-text-input"
                            placeholder="DD-MM-YYYY HH:MM (e.g., 25-06-2026 11:40)"
                        />
                        <button
                            type="button"
                            onClick={(e) => {
                                const hiddenPicker = e.currentTarget.nextSibling;
                                if (hiddenPicker) {
                                    try {
                                        hiddenPicker.showPicker();
                                    } catch (err) {
                                        hiddenPicker.focus();
                                        hiddenPicker.click();
                                    }
                                }
                            }}
                            style={{
                                position: "absolute",
                                right: "8px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                color: "#64748b",
                                padding: "4px"
                            }}
                            title="Open Date-Time Picker"
                        >
                            <Calendar size={18} />
                        </button>
                        <input
                            type="datetime-local"
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val) {
                                    const d = new Date(val);
                                    if (!isNaN(d.getTime())) {
                                        const day = String(d.getDate()).padStart(2, "0");
                                        const month = String(d.getMonth() + 1).padStart(2, "0");
                                        const year = d.getFullYear();
                                        const hh = String(d.getHours()).padStart(2, "0");
                                        const min = String(d.getMinutes()).padStart(2, "0");
                                        const formatted = `${day}-${month}-${year} ${hh}:${min}`;
                                        handleManualInputChange({ target: { name: key, value: formatted } });
                                    }
                                }
                            }}
                            style={{
                                position: "absolute",
                                width: 0,
                                height: 0,
                                opacity: 0,
                                pointerEvents: "none"
                            }}
                        />
                    </div>
                ) : (
                    <input
                        type={getInputType(key)}
                        name={key}
                        value={getInputValue(key, formData[key])}
                        onChange={handleValueChangeIntercept}
                        style={fieldBaseStyle}
                        className="modal-text-input"
                    />
                )}
            </div>
        );
    };

    return (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, backdropFilter: "blur(4px)" }}>
            <div style={{ backgroundColor: "white", borderRadius: "12px", width: "85%", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)" }}>

                {/* Modal Header Header Layout */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 28px", borderBottom: "1px solid #e2e8f0", backgroundColor: "#f8fafc", borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={{ padding: "8px", backgroundColor: "#e0f2fe", borderRadius: "8px", color: "#0284c7", display: "flex", alignItems: "center" }}>
                            <FileText size={20} />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#0f172a" }}>New Manual Production Entry</h3>
                            <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#64748b" }}>Create real-time database appraisal file trackers safely</p>
                        </div>
                    </div>
                    <button onClick={() => setShow(false)} style={{ padding: "6px", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#fff", border: "1px solid #e2e8f0", color: "#64748b", transition: "all 0.2s" }} className="close-hover-btn">
                        <X size={18} />
                    </button>
                </div>

                {/* Form Main Body Scrolling Container Panel Block Container Layout */}
                <form onSubmit={submitManualEntry} style={{ flex: 1, overflowY: "auto", padding: "28px" }}>

                    {/* PARTITION GROUP ROW LAYER 1: Core File Meta Definitions */}
                    <div style={{ marginBottom: "30px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", paddingBottom: "8px", borderBottom: "1px solid #f1f5f9", color: "#0284c7" }}>
                            <FileText size={16} />
                            <span style={{ fontSize: "14px", fontWeight: "700", letterSpacing: "0.5px" }}>1. File Metadata & Core Registration Info</span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px 16px" }}>
                            {metaKeys.map(renderFieldCard)}
                        </div>
                    </div>

                    {/* PARTITION GROUP ROW LAYER 2: Value Validation Calculations Processing Metadata */}
                    <div style={{ marginBottom: "30px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", paddingBottom: "8px", borderBottom: "1px solid #f1f5f9", color: "#b45309" }}>
                            <ClipboardCheck size={16} />
                            <span style={{ fontSize: "14px", fontWeight: "700", letterSpacing: "0.5px" }}>2. Valuation Parameters & Appraisal Findings</span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px 16px" }}>
                            {processingKeys.map(renderFieldCard)}
                        </div>
                    </div>

                    {/* PARTITION GROUP ROW LAYER 3: Internal Log Tracking Variables Timestamps Matrices */}
                    <div style={{ marginBottom: "15px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", paddingBottom: "8px", borderBottom: "1px solid #f1f5f9", color: "#475569" }}>
                            <Clock size={16} />
                            <span style={{ fontSize: "14px", fontWeight: "700", letterSpacing: "0.5px" }}>3. Production Timestamps & Timeline Logging Variables</span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px 16px" }}>
                            {loggingKeys.map(renderFieldCard)}
                        </div>
                    </div>

                    {/* Footer Operations Actions Controls Buttons Layer Spacer */}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", borderTop: "1px solid #e2e8f0", paddingTop: "20px", marginTop: "24px" }}>
                        <button type="button" onClick={() => setShow(false)} style={{ padding: "10px 24px", backgroundColor: "white", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: "600" }}>
                            Cancel Action
                        </button>
                        <button type="submit" style={{ padding: "10px 32px", backgroundColor: "#0284c7", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: "600", boxShadow: "0 4px 6px -1px rgba(2, 132, 199, 0.2)" }}>
                            Save Production Record
                        </button>
                    </div>
                </form>
            </div>

            <style>
                {`
                    .modal-text-input:focus, .modal-select-input:focus {
                        border-color: #0284c7 !important;
                        background-color: #fff !important;
                        box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.15) !important;
                    }
                    .close-hover-btn:hover {
                        background-color: #f1f5f9 !important;
                        color: #0f172a !important;
                    }
                `}
            </style>
        </div>
    );
};

export default ManualEntryModal;
